import React, { useState, useMemo, useCallback } from "react";
import EventRow from "./EventRow";
import { SourceBreakdown, EngagementFooter } from "./components";
import EmptyState from "./EmptyState";
import LoadingState from "./LoadingState";

/**
 * @param {Object}      props
 * @param {EventStat[]} props.events
 * @param {number} props.range
 * @param {Boolean} props.isLoading
 * @param {function} props.onRangeChange
 */
export default function PastEventsTab({
  events,
  range,
  onRangeChange,
  isLoading,
}) {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const sharedMax = useMemo(
    () => Math.max(...events.map((e) => Math.max(e.impressions, e.views)), 1),
    [events]
  );

  const toggleExpand = useCallback(
    (id) => () => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    },
    []
  );

  const records = useMemo(
    () =>
      events.map((event) => {
        const isExpanded = expandedIds.has(event.event_uuid);
        return (
          <React.Fragment key={event.event_uuid}>
            <EventRow
              event={event}
              sharedMax={sharedMax}
              isExpanded={isExpanded}
              onToggle={toggleExpand(event.event_uuid)}
              variant="past"
            />
            {isExpanded && (
              <tr className="eew-expand-row">
                <td colSpan={5}>
                  <SourceBreakdown sources={event.sources} />
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      }),
    [events, expandedIds, sharedMax, toggleExpand]
  );

  const hasEvents = events.length > 0;

  const body = isLoading ? (
    <LoadingState />
  ) : hasEvents ? (
    <table className="eew-table">
      <colgroup>
        <col className="eew-col-event" />
        <col className="eew-col-metric" />
        <col className="eew-col-metric" />
        <col className="eew-col-when" />
        <col className="eew-col-chevron" />
      </colgroup>
      <thead>
        <tr>
          <th>Event</th>
          <th className="text-right">Impressions</th>
          <th className="text-right">Views</th>
          <th className="text-right">View rate</th>
          <th className="text-right">
            <span className="eew-sr-only">Expand</span>
          </th>
        </tr>
      </thead>
      <tbody>{records}</tbody>
    </table>
  ) : (
    <EmptyState days={range} onRangeChange={onRangeChange} />
  );

  return (
    <>
      {body}
      {!isLoading && hasEvents && (
        <EngagementFooter>
          Bars are relative to the busiest event in view (
          {sharedMax.toLocaleString()} impressions). View rate is views ÷
          impressions.
        </EngagementFooter>
      )}
    </>
  );
}
